import React from 'react';
import PropTypes from 'prop-types';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import AudioPlayer from '@components/AudioPlayer';
import { getIconsByType, iconTypes } from '@services/icons';
import { formatDuration } from './utils';
import useVoiceMessageComposer from './useVoiceMessageComposer';
import {
  VoiceActionButton,
  VoiceActionCTA,
  VoicePreview,
  VoiceButtonLoader,
  RecordingPanel,
  RecordingInfo,
  RecordingDot,
  RecordingMeta,
  RecordingDuration,
} from './styles';

const MicrophoneIcon = getIconsByType(iconTypes.MICROPHONE);

const VoiceMessageComposer = ({
  t,
  isAreaDisabled,
  hasText,
  createVoiceFile,
  sendVoiceMessage,
  onVoiceDraftStateChange,
  children,
}) => {
  const {
    isRecording,
    isUploadingVoice,
    recordingDurationSeconds,
    voicePreviewUrl,
    voiceError,
    hasVoiceDraft,
    isVoiceModeActive,
    voiceActionState,
    handleCancelVoice,
  } = useVoiceMessageComposer({
    t,
    isAreaDisabled,
    hasText,
    createVoiceFile,
    sendVoiceMessage,
    onVoiceDraftStateChange,
  });

  const {
    voiceActionLabel,
    voiceActionHandler,
    voiceActionDisabled,
    isVoiceActionDanger,
    voiceActionTitle,
  } = voiceActionState;

  const renderVoiceInput = isVoiceModeActive ? (
    <RecordingPanel>
      {hasVoiceDraft ? (
        <VoicePreview>
          <AudioPlayer src={voicePreviewUrl} />
          <RecordingMeta>
            <Text size={TEXT_SIZE.T4} textColor={TEXT_COLOR.NORMAL}>
              {t('Informative.Notifying.voiceMessage')}
            </Text>
            <RecordingDuration>
              <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED}>
                {formatDuration(recordingDurationSeconds)}
              </Text>
            </RecordingDuration>
          </RecordingMeta>
        </VoicePreview>
      ) : (
        <RecordingInfo>
          <RecordingDot />
          <RecordingMeta>
            <Text size={TEXT_SIZE.T4} textColor={TEXT_COLOR.NORMAL}>
              {t('Common.Statuses.recording')}
            </Text>
            <RecordingDuration>
              <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED}>
                {formatDuration(recordingDurationSeconds)}
              </Text>
            </RecordingDuration>
          </RecordingMeta>
        </RecordingInfo>
      )}
    </RecordingPanel>
  ) : null;

  const renderActions = (
    <>
      {isVoiceModeActive ? (
        <VoiceActionCTA
          action
          onClick={voiceActionHandler}
          disabled={voiceActionDisabled}
        >
          {isUploadingVoice ? (
            <>
              <VoiceButtonLoader />
              {voiceActionLabel}
            </>
          ) : voiceActionLabel}
        </VoiceActionCTA>
      ) : (
        <VoiceActionButton
          type="button"
          onClick={voiceActionHandler}
          disabled={voiceActionDisabled}
          variant={isVoiceActionDanger ? 'danger' : 'default'}
          title={voiceActionTitle}
        >
          <MicrophoneIcon />
        </VoiceActionButton>
      )}
      {(isRecording || hasVoiceDraft) && (
        <VoiceActionCTA
          secondary
          onClick={handleCancelVoice}
          disabled={isUploadingVoice}
        >
          {t('Actions.Instance.cancel')}
        </VoiceActionCTA>
      )}
    </>
  );

  return children({
    isVoiceModeActive,
    isUploadingVoice,
    hasVoiceDraft,
    isRecording,
    voiceError,
    renderActions,
    renderVoiceInput,
  });
};

VoiceMessageComposer.propTypes = {
  t: PropTypes.func.isRequired,
  isAreaDisabled: PropTypes.bool,
  hasText: PropTypes.bool,
  createVoiceFile: PropTypes.func.isRequired,
  sendVoiceMessage: PropTypes.func.isRequired,
  onVoiceDraftStateChange: PropTypes.func,
  children: PropTypes.func.isRequired,
};

VoiceMessageComposer.defaultProps = {
  isAreaDisabled: false,
  hasText: false,
  onVoiceDraftStateChange: undefined,
};

export default VoiceMessageComposer;
